import React, { useEffect, useRef } from 'react';
import { Box, List, ListItem, Typography } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { useAdobeAnalytics } from '../../providers/AdobeAnalytics';
import useOnClickOutside from '../../hooks/useOnClickOutside';
import { useDispatch } from 'react-redux';
import { resetAllReleaseItems } from '../../redux/slices/staticPagesSlice';
import { useTranslation } from 'react-i18next';

const DeskTopDropDownMenu = ({
  dropDownMenu,
  coords,
  setDropDown,
  activeDropDown,
  keyboard,
  setKeyboard,
  tabRefs,
  categories,
  dropDownMenuRef,
  menuItemsRef
}) => {
  //Refs
  const dropDownRef = useRef<HTMLDivElement>(null);
  const firstItemRef = useRef<HTMLLIElement>(null);
  const { i18n } = useTranslation();
  const { adobeSiteInteraction, adobeAnalyticsSectionClick } =
    useAdobeAnalytics();
  const navigate = useNavigate();
  const dispatch = useDispatch()

  useEffect(() => {
    if (keyboard) {
      dropDownRef?.current?.focus();
      setKeyboard(false);
    }
  }, [keyboard, setKeyboard]);

  // useEffect(() => {
  //   if (dropDownMenuRef.current) {
  //     firstItemRef.current?.focus();
  //   }
  // }, [dropDownMenuRef, menuItemsRef]);

  useOnClickOutside(dropDownRef, () => {
    setDropDown(false);
  });

  let prevCategory = '';
  let colPos = 0;
  let rowPos = 1;

  const doClickActions = (label) => {
    dispatch(resetAllReleaseItems())
    window.scrollTo(0, 0);
    adobeAnalyticsSectionClick('header nav');
    const isSandbox = label === 'Sandbox' || label === 'Bac à sable';
    isSandbox && adobeSiteInteraction('sandbox-header');
  };

  const doKeyPressActions = (e, path, index) => {
    adobeAnalyticsSectionClick('header nav');
    dispatch(resetAllReleaseItems())

    if (e.shiftKey && e.key === 'Tab') {
      if (index === 0) {
        e.preventDefault();
        tabRefs.current[activeDropDown].focus();
      }
    }
    if (e.key === 'Tab') {
      e.preventDefault();
      if (index === dropDownMenu?.menu?.length - 1) {
        if (tabRefs.current.length - 1 !== activeDropDown) {
          tabRefs.current[activeDropDown + 1].focus();
        } else {
          setDropDown(false);
        }
      } else {
        menuItemsRef.current[index + 1].focus();
      }
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (index !== dropDownMenu?.menu?.length - 1) {
        menuItemsRef.current[index + 1].focus();
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (index === 2 && firstItemRef.current) {
        firstItemRef.current.focus();
      } else if (index !== 0) {
        menuItemsRef.current[index - 1].focus();
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      navigate(
        path.charAt(0) !== '/'
          ? path
          : `/${i18n.language}${path}`
      );
      setDropDown(false);
    }
  };

  const getLink = (path, label, index) =>
    path.charAt(0) !== '/' ? (
      <a className="dtMenuItem" href={path} target="_blank" rel="noreferrer">
        <Typography>{label}</Typography>
      </a>
    ) : (
      <Link
        className="dtMenuItem"
        to={`/${i18n.language}${path}`}
        onClick={() => doClickActions(label)}
        onKeyDown={(e) => doKeyPressActions(e, path, index)}
      >
        <Typography>{label}</Typography>
      </Link>
    );

  return (
    <Box
      className="dropDownMenu"
      sx={{
        top: coords.y + 60,
        left: coords.x
      }}
      ref={dropDownMenuRef}
      tabIndex={0}
      onMouseLeave={() => setDropDown(false)}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          setDropDown(false);
          tabRefs.current[activeDropDown].focus();
        }
      }}
    >
      <span
        className="arrow"
        style={{ '--left': `${coords.width / 2 - 5}px` } as any}
      ></span>
      <List
        role="menu"
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setDropDown(false);
            tabRefs.current[activeDropDown].focus();
          }
        }}
        sx={{ display: 'grid' }}
      >
        {dropDownMenu?.menu?.map((subMenu, index) => {

          const lastIndex = dropDownMenu?.menu.length;
          const currCategory =
            categories[subMenu.parentMenuName][subMenu.category];
          const showSubHeader = prevCategory !== currCategory;
          colPos = showSubHeader ? colPos + 1 : colPos;
          rowPos = !showSubHeader ? rowPos + 1 : 2;

          prevCategory = currCategory;

          return (
            <React.Fragment key={`box2-${index}`}>
              {showSubHeader && (
                <Box
                  className="mainNavSubHead"
                  sx={{
                    gridRowStart: 1,
                    gridRowEnd: 1,
                    gridColumnStart: colPos,
                    gridColumnEnd: colPos
                  }}
                >
                  {currCategory}
                </Box>
              )}
              <ListItem
                ref={
                  index === 1
                    ? firstItemRef
                    : (el) => (menuItemsRef.current[index] = el)
                }
                className="menuItem"
                aria-label={subMenu.label}
                tabIndex={0}
                style={{
                  ...(index !== lastIndex - 1 && {
                    borderBottom: '1px solid #DDECF5'
                  })
                }}
                sx={{
                  gridRowStart: rowPos,
                  gridRowEnd: rowPos,
                  gridColumnStart: colPos,
                  gridColumnEnd: colPos
                }}
                onKeyDown={(e) => doKeyPressActions(e, subMenu.path, index)}
                onClick={() => {
                  if (subMenu.path.charAt(0) !== '/') {
                    window.open(subMenu.path, '_blank', 'noopener, noreferrer');
                  } else {
                    navigate(`/${i18n.language}${subMenu.path}`);
                  }
                  setDropDown(false);
                }}
              >
                {getLink(subMenu.path, subMenu.label, index)}
              </ListItem>
            </React.Fragment>
          );
        })}
      </List>
    </Box>
  );
};

export default DeskTopDropDownMenu;
